// Parking Selection card › Select Parking, Old parking flow (M5-F6; rows M5.29, DC-176), prototype pkOldModal.
// Today's CRM Select Parking (SelectParkingModal.jsx) for a CRM 2.0 booking whose parking flow is Old: parking type,
// then up to 4 slots by floor (number, L × B × H; taken ones greyed out), no price; Skip only when the setting allows
// it, else Cancel; Save → confirm → Final Review. The approved parking upgrade (typed in Change Plan) is shown on top.
// Options and the save are on the server (GET /crm2/parking/options, POST /crm2/parking/select | skip).
import React, { useEffect, useMemo, useState } from "react";
import { Box, Modal } from "@mui/material";
import Close from "../../Icons/Close";
import Button from "../../../Subcomponents/Button";
import ConfirmationAlert from "../../../Subcomponents/ConfirmationAlert";
import { crm2ParkingOptions, crm2ParkingSelect, crm2ParkingSkip } from "../../../Services/Crm2List";
import { dismissLoader, presentLoader } from "../../../Services/loader";
import { errorTost, successTost } from "../../../Services/Toast";
import { unitLine } from "../ui/cardParts";
import settingsCss from "../Settings/Crm2Settings.module.css";

const inr = (n) => `₹ ${Math.round(Number(n) || 0).toLocaleString("en-IN")}`;
const btnSec = { width: "fit-content", color: "#344054", backgroundColor: "#FFFFFF", border: "1px solid #D0D5DD", fontSize: "1.4rem", lineHeight: "2rem" };
const btnPri = { width: "fit-content", color: "#FFFFFF", backgroundColor: "#155EEF", border: "1px solid #155EEF", fontSize: "1.4rem", lineHeight: "2rem" };
const hint = { fontSize: "1.3rem", color: "#667085" };

export default function Crm2OldParkingModal({ card, onClose, onDone }) {
  const apId = card.allotmentPaymentId;
  const [o, setO] = useState(null);
  const [type, setType] = useState("");
  const [sel, setSel] = useState([]);
  const [ask, setAsk] = useState(null); // "save" | "skip"

  const load = () =>
    crm2ParkingOptions(apId)
      .then((d) => {
        console.log("[crm2][Crm2OldParkingModal] options", { allotmentPaymentId: apId, flow: d?.flow, slots: d?.slots?.length, current: d?.current?.length });
        setO(d);
        const cur = d?.current || [];
        if (cur.length) {
          setType(cur[0].type || "");
          setSel(cur.map((x) => x.id));
        }
      })
      .catch((e) => {
        errorTost(e?.response?.data?.message || "Could not load the parking. Please try again.");
        onClose();
      });
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apId]);

  const ofType = useMemo(() => (o?.slots || []).filter((x) => x.type === type), [o, type]);
  const floors = [...new Set(ofType.map((x) => x.floor))];
  const byId = useMemo(() => new Map((o?.slots || []).map((x) => [x.id, x])), [o]);
  const pick = (x) => {
    if (sel.includes(x.id)) return setSel(sel.filter((y) => y !== x.id));
    if (sel.length < 4) setSel([...sel, x.id]);
  };

  const save = async (what) => {
    setAsk(null);
    presentLoader();
    try {
      const r = what === "skip" ? await crm2ParkingSkip(apId) : await crm2ParkingSelect(apId, { includedIds: sel, upgradeIds: [] });
      console.log("[crm2][Crm2OldParkingModal] saved", { allotmentPaymentId: apId, what, status: r?.status });
      successTost(what === "skip" ? "Parking skipped. Moved to Final Review" : "Parking confirmed. Moved to Final Review");
      onDone();
    } catch (e) {
      const message = e?.response?.data?.message || "Parking not saved. Please try again.";
      console.error("[crm2][Crm2OldParkingModal] save failed", { allotmentPaymentId: apId, what, status: e?.response?.status, code: e?.response?.data?.code, message });
      errorTost(message);
      if (e?.response?.data?.code === "SLOT_TAKEN") load();
    } finally {
      dismissLoader();
    }
  };

  const selText = sel
    .map((id) => byId.get(id))
    .filter(Boolean)
    .map((x) => `${x.number} - ${x.type} - Floor(${x.floor})`)
    .join(", ");
  const up = Number(o?.upgradeCost) || 0;

  return (
    <>
      <Modal open onClose={onClose} sx={{ ".MuiBackdrop-root": { background: "rgba(52, 64, 84, 0.7)" } }} disableAutoFocus>
        <Box className={settingsCss.modalWrapper} style={{ width: "72rem", maxWidth: "calc(100% - 3.2rem)" }}>
          <div className={settingsCss.modalHeader}>
            <span>Parking</span>
            <span className={settingsCss.modalClose} onClick={onClose} tabIndex={0} role="button" aria-label="Close">
              <Close width="1.2rem" height="1.2rem" />
            </span>
          </div>
          <div className={settingsCss.modalContent} style={{ maxHeight: "70vh", overflow: "auto" }}>
            {!o ? (
              <p style={{ ...hint, textAlign: "center", padding: "3rem 0" }}>Loading…</p>
            ) : (
              <>
                <p style={{ ...hint, margin: "0 0 .4rem" }}>Parking flow: Old (project setting) · today's CRM Select Parking</p>
                <div style={{ display: "flex", gap: "2.4rem", flexWrap: "wrap", marginBottom: "1.6rem", fontSize: "1.4rem" }}>
                  <span>
                    <span style={{ color: "#475467" }}>Customer Name:</span> <b>{card.leadName}</b>
                  </span>
                  <span>
                    <span style={{ color: "#475467" }}>Unit:</span> <b>{unitLine(card)}</b>
                  </span>
                </div>
                {up > 0 && (
                  <div style={{ background: "#EFF4FF", border: "1px solid #C7D7FD", color: "#3538CD", borderRadius: ".8rem", padding: "1rem 1.4rem", fontSize: "1.35rem", marginBottom: "1.2rem" }}>
                    Parking upgrade {inr(up)} approved by the reporting manager (in the agreement value). Pick the upgraded parking here.
                  </div>
                )}
                {!o.applicable ? (
                  <div style={{ background: "#FFFAEB", border: "1px solid #FEDF89", color: "#B54708", borderRadius: ".8rem", padding: "1rem 1.4rem", fontSize: "1.4rem" }}>{o.why}</div>
                ) : (
                  <>
                    <label style={{ display: "block", fontSize: "1.4rem", fontWeight: 500, color: "#344054", marginBottom: ".6rem" }} htmlFor="pot">
                      Parking Type <sup style={{ color: "red" }}>*</sup>
                    </label>
                    <select
                      id="pot"
                      aria-label="Parking Type"
                      value={type}
                      onChange={(e) => {
                        setType(e.target.value);
                        setSel([]);
                      }}
                      style={{ width: "100%", fontSize: "1.5rem", padding: ".9rem 1rem", border: "1px solid #D0D5DD", borderRadius: ".8rem", marginBottom: "1.6rem", color: "#101828", background: "#fff" }}
                    >
                      <option value="">Select</option>
                      {o.types.map((t) => (
                        <option key={t}>{t}</option>
                      ))}
                    </select>
                    {type &&
                      floors.map((f) => (
                        <div key={f} style={{ marginBottom: "1.6rem" }}>
                          <p style={{ fontSize: "1.4rem", fontWeight: 600, color: "#344054", marginBottom: ".8rem" }}>Parking Floor - {f}</p>
                          <div style={{ display: "flex", gap: ".8rem", flexWrap: "wrap" }}>
                            {ofType
                              .filter((x) => x.floor === f)
                              .map((x) => {
                                const on = sel.includes(x.id);
                                return (
                                  <button
                                    key={x.id}
                                    type="button"
                                    disabled={x.taken}
                                    title={`Length : ${x.length ?? "—"} m · Breadth : ${x.breadth ?? "—"} m · Height : ${x.height ?? "—"} m`}
                                    onClick={() => pick(x)}
                                    style={{ minWidth: "8rem", padding: ".6rem 1.2rem", fontSize: "1.4rem", borderRadius: ".8rem", cursor: x.taken ? "default" : "pointer", border: `1px solid ${on ? "#155EEF" : "#D0D5DD"}`, background: x.taken ? "#F2F4F7" : on ? "#EFF4FF" : "#fff", color: x.taken ? "#98A2B3" : on ? "#155EEF" : "#344054" }}
                                  >
                                    {x.number}
                                  </button>
                                );
                              })}
                          </div>
                        </div>
                      ))}
                    {type && <p style={hint}>Up to 4 parkings · taken parkings are greyed out</p>}
                    {sel.length > 0 && (
                      <p style={{ fontSize: "1.4rem", marginTop: "1.2rem" }}>
                        <b>Selected Parking -</b> {selText}
                      </p>
                    )}
                  </>
                )}
              </>
            )}
          </div>
          <div className={settingsCss.modalFooter}>
            {o?.allowSkip ? <Button style={btnSec} btnName="Skip" clicked={() => setAsk("skip")} /> : <Button style={btnSec} btnName="Cancel" clicked={onClose} />}
            <Button style={{ ...btnPri, opacity: sel.length ? 1 : 0.5 }} btnName="Save" disable={!sel.length} clicked={() => setAsk("save")} />
          </div>
        </Box>
      </Modal>
      {ask && (
        <ConfirmationAlert
          open
          close={() => setAsk(null)}
          buttonTitle={"Cancel"}
          title={"Warning"}
          subTitle={ask === "skip" ? `No parking for ${card.leadName || "this booking"}? The customer sees "No parking for this unit" and the stamp duty & charges payment opens.` : `Parking is selected for ${card.leadName || "this booking"}, do you want to proceed?`}
          returnRespButton={"Confirm"}
          confirm={(v) => (v === "confirmed" ? save(ask) : setAsk(null))}
        />
      )}
    </>
  );
}
